import { VegetationType } from "../../../types/vegetationType";
import { TenantVegetationType } from "../../../types/tenantVegetationType";
import { VegetationTypeBatch, VegetationTypeFields } from "../../../api/vegetationType";
import { UnitSystem, tonsPerAcreToTonsPerHectare, tonsPerHectareToTonsPerAcre } from "../../../utils/units";
import { hasSubclass, subclassCode, subclassSpecFor } from "../../../constants/modelSubclass";
import { SCOTT_BURGAN_MODEL_CLASS_NAME } from "../../../constants/scottBurgan";
import { FBP_MODEL_CLASS_NAME } from "../../../constants/fbp";

/** Years along the accumulation charts. */
export const T_MAX = 25;

/** Map colours offered in the side panel; a new type takes the next one. */
export const PALETTE = [
  "#00d4ad", "#7cb0ff", "#f2b84b", "#ff8080", "#c084fc", "#4ade80",
  "#fb923c", "#22d3ee", "#e88dc4", "#bca66a", "#826fa7", "#977326",
];

/** A vegetation type as the tab edits it: numbers kept as typed, fuel loads
 *  in the user's units, the model class and subclass as one value. */
export interface VtRow {
  /** String(id), or "new-N" for a type not saved yet. */
  key: string;
  id: number | null;
  name: string;
  mc: string;
  max: string;
  /** The stored load in t/ha behind ``max`` while its text is unchanged, so a
   *  saved or library value is sent back exactly rather than re-read from
   *  the rounded imperial text. */
  maxT?: number;
  rate: string;
  color: string;
  deleted?: boolean;
  fromLibrary?: boolean;
}

export type VtField = "name" | "color" | "mc" | "max" | "rate";

export interface VtChange {
  field: VtField | "added" | "deleted";
  from?: string;
  to?: string;
}

export interface VtErrors {
  name?: string;
  max?: string;
  rate?: string;
}

/** A draft row with what the tab knows about it. */
export interface RowInfo {
  r: VtRow;
  /** The saved row; none for a type added in this draft. */
  s?: VtRow;
  changes: VtChange[];
  fields: Partial<Record<VtField, boolean>>;
  errs: VtErrors;
  hasErr: boolean;
}

export const FIELD_LABEL: Record<VtChange["field"], string> = {
  name: "Name", color: "Colour", mc: "Model class", max: "Max fuel load", rate: "Accumulation rate", added: "Status", deleted: "Status",
};

// ---------------------------------------------------------------------------
// Numbers and units
// ---------------------------------------------------------------------------

export const num = (s: unknown): number => {
  if (s === null || s === undefined) return NaN;
  const t = String(s).trim();
  return t === "" ? NaN : Number(t);
};

const round = (v: number, dp: number) => Math.round(v * 10 ** dp) / 10 ** dp;

export const r1 = (v: number) => String(round(v, 1));

export const loadUnit = (u: UnitSystem) => (u === "imperial" ? "tons/acre" : "t/ha");

// Exact conversions: the display helpers round to whole numbers, which
// would change a load someone only looked at.
const toUserLoad = (tPerHa: number, u: UnitSystem) => (u === "imperial" ? tonsPerHectareToTonsPerAcre(tPerHa) : tPerHa);

/** A stored fuel load (t/ha) as the text in an input. */
export const loadToInput = (tPerHa: number, u: UnitSystem): string =>
  String(u === "imperial" ? round(toUserLoad(tPerHa, u), 2) : tPerHa);

/** A fuel load typed in the user's units, in t/ha for the server. */
export const loadFromInput = (v: string, u: UnitSystem): number =>
  u === "imperial" ? round(tonsPerAcreToTonsPerHectare(num(v)), 3) : num(v);

/** Highest max fuel load accepted, in t/ha. */
const MAX_LOAD_T = 100;

/** Highest max fuel load accepted, in the user's units. */
export const maxLoad = (u: UnitSystem) => round(toUserLoad(MAX_LOAD_T, u), 1);

/** A row's max fuel load in t/ha: the stored value while the text still
 *  shows it, else what was typed. */
export const loadOf = (r: { max: string; maxT?: number }, u: UnitSystem): number =>
  r.maxT !== undefined && Number.isFinite(r.maxT) && r.max === loadToInput(r.maxT, u) ? r.maxT : loadFromInput(r.max, u);

/** Top of the max fuel load slider (50 t/ha), in the user's units. */
export const sliderLoad = (u: UnitSystem) => round(toUserLoad(50, u), 1);

// ---------------------------------------------------------------------------
// Model class: "" (no fire model: the type doesn't burn), "Grassland", or
// "Scott & Burgan|102"
// ---------------------------------------------------------------------------

export const mcValue = (name: string | null | undefined, sub: number | null | undefined): string =>
  !name ? "" : hasSubclass(name) ? `${name}|${sub ?? ""}` : name;

export const mcParts = (v: string): { name: string | null; sub: number | null } => {
  if (!v) return { name: null, sub: null };
  const [name, s] = v.split("|");
  return { name, sub: s ? Number(s) : null };
};

/** What the server's model class 0 means. */
export const NO_MODEL_LABEL = "Doesn't burn";

const familyShort = (name: string) =>
  name === SCOTT_BURGAN_MODEL_CLASS_NAME ? "S&B" : name === FBP_MODEL_CLASS_NAME ? "FBP" : name;

export const mcLabel = (v: string): string => {
  const { name, sub } = mcParts(v);
  if (!name) return NO_MODEL_LABEL;
  const option = subclassSpecFor(name)?.options.find((o) => o.value === sub);
  return option ? `${familyShort(name)} ${option.label}` : name;
};

/** Compact label for a chip: "S&B GR1", "Wet Eucalypt". */
export const mcShort = (v: string): string => {
  const { name, sub } = mcParts(v);
  const code = subclassCode(name, sub);
  return name && code ? `${familyShort(name)} ${code}` : mcLabel(v);
};

export interface McGroup {
  label: string;
  options: { value: string; label: string }[];
}

/** The model class menu: plain classes, then a group per subclassed class. */
export const mcGroups = (classes: string[]): McGroup[] => {
  const groups: McGroup[] = [
    { label: "Australian fuel types", options: classes.filter((c) => !hasSubclass(c)).map((c) => ({ value: c, label: c })) },
  ];
  classes.filter(hasSubclass).forEach((c) => {
    groups.push({
      label: c === SCOTT_BURGAN_MODEL_CLASS_NAME ? "Scott & Burgan 40" : c,
      options: (subclassSpecFor(c)?.options ?? []).map((o) => ({ value: mcValue(c, o.value), label: o.label })),
    });
  });
  return groups;
};

/** The model class a new blank type starts with: the first one offered. */
export const defaultMc = (groups: McGroup[]): string => groups.find((g) => g.options.length)?.options[0].value ?? "";

/** Model class filter: "*" all, "" no fire model, else a class name (any subclass). */
export const matchesMcFilter = (mc: string, filter: string) =>
  filter === "*" || (mcParts(mc).name ?? "") === filter;

// ---------------------------------------------------------------------------
// Fuel accumulation: L(t) = Lmax * (1 - e^(-k t))
// ---------------------------------------------------------------------------

export const loadAt = (L: number, k: number, t: number) => L * (1 - Math.exp(-k * t));

/** Years to reach (1 - 1/f) of max: f = 2 for half, 10 for 90%. */
export const yearsTo = (k: number, f: number): string => {
  if (!(k > 0)) return "Never";
  const y = Math.log(f) / k;
  if (y >= 100) return "100+ yrs";
  return `${y < 10 ? y.toFixed(1) : Math.round(y)} yrs`;
};

/** SVG path of the curve across a w x h box whose top is yMax. */
export const curvePath = (L: number, k: number, yMax: number, w: number, h: number): string => {
  let d = "";
  for (let i = 0; i <= 60; i++) {
    const t = (i * T_MAX) / 60;
    const y = h - Math.min(loadAt(L, k, t) / yMax, 1) * h;
    d += `${i ? "L" : "M"}${((t / T_MAX) * w).toFixed(1)} ${y.toFixed(1)}`;
  }
  return d;
};

export const areaPath = (L: number, k: number, yMax: number, w: number, h: number) =>
  `${curvePath(L, k, yMax, w, h)}L${w} ${h}L0 ${h}Z`;

/** A round axis step at or above ``raw``. */
export const niceStep = (raw: number) => [0.5, 1, 2, 2.5, 5, 10, 20, 25, 50].find((c) => c >= raw) ?? 50;

// ---------------------------------------------------------------------------
// Rows: from the server, checks, differences, the save batch
// ---------------------------------------------------------------------------

export const rowFromServer = (vt: VegetationType, u: UnitSystem): VtRow => ({
  key: String(vt.id),
  id: vt.id,
  name: vt.veg_class,
  mc: mcValue(vt.model_class_name, vt.model_subclass),
  max: loadToInput(Number(vt.max_fuel_load), u),
  maxT: Number(vt.max_fuel_load),
  rate: String(vt.accumulation_rate),
  color: (vt.color ?? PALETTE[0]).toLowerCase(),
});

export const rowFromLibrary = (lib: TenantVegetationType, key: string, color: string, u: UnitSystem): VtRow => ({
  key,
  id: null,
  name: lib.veg_class,
  mc: mcValue(lib.model_class_name, lib.model_subclass),
  max: loadToInput(Number(lib.max_fuel_load ?? 20), u),
  maxT: Number(lib.max_fuel_load ?? 20),
  rate: String(lib.accumulation_rate ?? 0.07),
  color,
  fromLibrary: true,
});

export const nameKey = (name: string) => name.trim().toLowerCase();

/** ``check`` limits the range checks to the values someone entered, so a
 *  stored value outside them (an import, say) never blocks a save. */
export const validateRow = (
  r: { name: string; max: string; maxT?: number; rate: string },
  u: UnitSystem,
  duplicate = false,
  check: { max: boolean; rate: boolean } = { max: true, rate: true },
): VtErrors => {
  const e: VtErrors = {};
  if (!r.name.trim()) e.name = "Name is required";
  else if (duplicate) e.name = "Another vegetation type already uses this name";
  // Compared in t/ha: the imperial text has more places than the cap.
  const L = loadOf(r, u);
  if (check.max && (!Number.isFinite(L) || L < 0 || L > MAX_LOAD_T)) e.max = `Enter a value from 0 to ${maxLoad(u)} ${loadUnit(u)}`;
  const k = num(r.rate);
  if (check.rate && (!Number.isFinite(k) || k < 0 || k > 1)) e.rate = "Enter a rate from 0 to 1";
  return e;
};

export const diffRow = (r: VtRow, s: VtRow | undefined): VtChange[] => {
  if (!s) return [{ field: "added" }];
  if (r.deleted) return [{ field: "deleted" }];
  const out: VtChange[] = [];
  if (r.name.trim() !== s.name.trim()) out.push({ field: "name", from: s.name, to: r.name });
  if (r.color.toLowerCase() !== s.color.toLowerCase()) out.push({ field: "color", from: s.color, to: r.color });
  if (r.mc !== s.mc) out.push({ field: "mc", from: s.mc, to: r.mc });
  if (num(r.max) !== num(s.max)) out.push({ field: "max", from: s.max, to: r.max });
  if (num(r.rate) !== num(s.rate)) out.push({ field: "rate", from: s.rate, to: r.rate });
  return out;
};

export const fmtField = (field: VtField, v: string, u: UnitSystem): string => {
  if (field === "mc") return mcLabel(v);
  if (field === "max") return Number.isFinite(num(v)) ? `${num(v)} ${loadUnit(u)}` : v || "—";
  if (field === "rate") return Number.isFinite(num(v)) ? String(num(v)) : v || "—";
  if (field === "color") return v.toUpperCase();
  return v;
};

const mcFields = (mc: string): VegetationTypeFields => {
  const { name, sub } = mcParts(mc);
  return name ? { model_class_name: name, model_subclass: hasSubclass(name) ? sub : null } : { model_class: 0, model_subclass: null };
};

/** What Save sends: removals, changed fields of existing types, new types. */
export const buildBatch = (draft: VtRow[], saved: VtRow[], u: UnitSystem): VegetationTypeBatch => {
  const savedByKey = new Map(saved.map((s) => [s.key, s]));
  const batch: VegetationTypeBatch = { delete: [], update: [], create: [] };
  draft.forEach((r) => {
    const s = savedByKey.get(r.key);
    if (r.id !== null && r.deleted) {
      batch.delete.push(r.id);
      return;
    }
    if (r.deleted) return;
    if (r.id === null || !s) {
      batch.create.push({
        key: r.key, veg_class: r.name.trim(), color: r.color, ...mcFields(r.mc),
        max_fuel_load: loadOf(r, u), accumulation_rate: num(r.rate),
      });
      return;
    }
    const changes = diffRow(r, s);
    if (!changes.length) return;
    const fields: VegetationTypeFields = {};
    changes.forEach((c) => {
      if (c.field === "name") {
        fields.veg_class = r.name.trim();
        // A colour nobody picked follows the name; keep the one on screen.
        fields.color = r.color;
      }
      if (c.field === "color") fields.color = r.color;
      if (c.field === "mc") Object.assign(fields, mcFields(r.mc));
      if (c.field === "max") fields.max_fuel_load = loadOf(r, u);
      if (c.field === "rate") fields.accumulation_rate = num(r.rate);
    });
    batch.update.push({ id: r.id, ...fields });
  });
  // The server applies updates one at a time and names are unique, so a
  // type taking a name another type gives up in this save (A and B
  // swapped) would collide. Those types first move to a temporary name.
  const savedName = new Map(saved.map((s) => [s.id, s.name]));
  const taken = new Set(batch.update.filter((f) => f.veg_class !== undefined).map((f) => f.veg_class));
  const parked = batch.update
    .filter((f) => f.veg_class !== undefined && taken.has(savedName.get(f.id)))
    .map((f) => ({ id: f.id, veg_class: `__renaming_${f.id}__` }));
  batch.update = [...parked, ...batch.update];
  return batch;
};

export const plural = (n: number, one: string, many?: string) => `${n} ${n === 1 ? one : many ?? `${one}s`}`;
